import { geldText } from '@symphenia/shared-types'
import { KOSTENART_KURZ } from '../lib/kalkulation-labels'
import type { CalcClosing } from '../types/calculation.types'

const prozent = (p: number | undefined) => (p == null ? '' : `${p.toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`)

/**
 * AP-37 4.10 — **Schlussblatt**: von den Einzelkosten zur Angebotssumme, jede
 * Zeile des Zuschlagsschemas mit ihrer Wirkung in Euro. Nur lesend; mit
 * `vorher` stehen die Beträge des gespeicherten Stands daneben (Vorschau im
 * Reiter Sätze & Aufschläge).
 */
export function Schlussblatt({ closing, vorher, onAendern }: { closing: CalcClosing; vorher?: CalcClosing | null; onAendern?: () => void }) {
  const alt = (i: number) => vorher?.lines[i]
  const fett = new Set(['COST_TOTAL', 'PRICE_TOTAL', 'PRICE_AFTER_DISCOUNT'])
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Schlussblatt</h3>
        {onAendern && (
          <button type="button" className="text-xs text-primary hover:underline" onClick={onAendern}>
            ändern in Sätze & Aufschläge
          </button>
        )}
      </div>
      <table className="w-full text-sm">
        <tbody>
          {closing.lines.map((z, i) => {
            const a = alt(i)
            const anders = vorher && a && a.art === z.art && Math.abs(a.amount - z.amount) >= 0.005
            return (
              <tr key={`${z.art}-${z.surchargeId ?? z.kind ?? i}`} className={`${fett.has(z.art) ? 'border-t border-border font-semibold' : ''}`}>
                <td className="py-0.5 pr-2">
                  {z.label}
                  {z.art === 'SURCHARGE' && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {prozent(z.percent)} · {z.basis === 'RUNNING' ? 'laufend' : 'direkt'} auf {(z.appliesTo ?? []).map((k) => KOSTENART_KURZ[k]).join('+')}
                      {z.overrides ? ` · ${z.overrides} Position(en) abweichend` : ''}
                    </span>
                  )}
                </td>
                {vorher && <td className="w-28 py-0.5 text-right tabular-nums text-muted-foreground">{a && a.art === z.art ? geldText(a.amount) : ''}</td>}
                <td className={`w-32 py-0.5 text-right tabular-nums ${anders ? 'text-sky-800 dark:text-sky-300' : ''}`}>{geldText(z.amount)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="mt-2 flex flex-wrap justify-between gap-2 border-t border-border pt-2 text-sm">
        <span className="text-muted-foreground">Deckungsbeitrag</span>
        <span className="tabular-nums">
          {vorher && <span className="mr-2 text-muted-foreground">{geldText(vorher.contributionMargin)} →</span>}
          {geldText(closing.contributionMargin)}
          {closing.contributionMarginPercent != null && ` · ${closing.contributionMarginPercent.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %`}
        </span>
      </div>
      {closing.rateMissing > 0 && <p className="mt-1 text-xs text-destructive">{closing.rateMissing} Position(en) mit Arbeitsschritt ohne Satz — Lohn dafür 0.</p>}
    </div>
  )
}
